import { z } from "zod";
import { LocaleSchema } from "./i18n.js";
/**
 * MOD-3 — plateforme d'où provient le `deviceId` : "web" (navigateur), "ios",
 * "android". Le `deviceId` est un UUID opaque, de forme identique partout : sans
 * cette information, la console de modération ne peut pas dire si elle bloque un
 * navigateur (que l'utilisateur change en 10 secondes) ou un téléphone.
 *
 * OPTIONNEL par construction : un client N-1 qui ne l'envoie pas doit continuer
 * de se connecter normalement (l'appareil reste « inconnu » côté modération).
 */
export const DevicePlatformSchema = z.enum(["web", "ios", "android"]);
export const SignupSchema = z.object({
    email: z.string().trim().toLowerCase().max(254).email(),
    password: z.string().min(8),
    displayName: z.string().min(2),
    deviceId: z.string().optional(),
    devicePlatform: DevicePlatformSchema.optional(),
    ageCertified: z.literal(true, { errorMap: () => ({ message: "Vous devez certifier avoir au moins 13 ans" }) }),
    termsAccepted: z.literal(true, { errorMap: () => ({ message: "Vous devez accepter les conditions générales d'utilisation" }) }),
    captchaToken: z.string({ required_error: "Validation captcha requise" }).min(1, "Validation captcha requise"),
    referrerId: z.string().optional(),
    /** Piste Jeux (L3-3/G4) — token d'un lien de défi : à l'inscription,
     *  contact auto + match créé + conversation ouverte avec le challenger. */
    challenge: z.string().max(64).optional(),
    /** US3 (G5) — conversion d'un INVITÉ : le JWT invité (claim guest) encore
     *  détenu par le client après la partie sans compte. Vérifié serveur, il
     *  re-parente le match de l'invité vers le compte créé. Jamais stocké. */
    guestToken: z.string().max(2048).optional(),
    // I18N-1 — normalisé (`fr-FR`→`fr`) puis validé contre SUPPORTED_LANGUAGES :
    // une locale inconnue est rejetée en 400, jamais écrite en base.
    locale: LocaleSchema.optional(),
});
export const LoginSchema = z.object({
    email: z.string().trim().toLowerCase().email(),
    password: z.string().min(1),
    deviceId: z.string().optional(),
    devicePlatform: DevicePlatformSchema.optional(),
});
export const RefreshSchema = z.object({
    refreshToken: z.string().optional(), // Web sends via cookie, native via body
});
export const AuthResponseSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string().optional(), // Exists for native clients
    user: z.object({
        id: z.string(),
        email: z.string(),
        displayName: z.string(),
        isEmailVerified: z.boolean(),
        phone: z.string().nullable().optional(),
        isPhoneVerified: z.boolean().optional(),
        createdAt: z.string().optional(), // Add createdAt so user can see "membre depuis"
        instagram: z.string().nullable().optional(),
        tiktok: z.string().nullable().optional(),
        whatsapp: z.string().nullable().optional(),
        revealPhoneToContacts: z.boolean().optional(),
        revealEmailToContacts: z.boolean().optional(),
        showOnlineStatus: z.boolean().optional(),
        emailNotifications: z.boolean().optional(),
        locale: z.string().optional(),
        isAdmin: z.boolean().optional(),
        ageCertifiedAt: z.string().nullable().optional(),
        acceptedTermsAt: z.string().nullable().optional(),
        acceptedTermsVersion: z.number().nullable().optional(),
        // BE-11 / FE-5 — MA photo de profil. Les URL servantes (jamais une clé de
        // stockage), pour que le Compte puisse afficher la photo courante sans
        // repasser par la résolution groupée. Absent = pas de photo.
        avatar: z.object({ url: z.string().url(), thumbUrl: z.string().url() }).nullable().optional(),
        // GA-6 — choix explicite du jeu favori (null = calcul automatique).
        favoriteGameType: z.string().nullable().optional(),
    }),
});
/** Voir MAX_DESTINATION_LENGTH (rest.ts) : ces adresses fabriquent des noms de
 *  clés Redis, une adresse non bornée sature Redis (audit du 25 juil. 2026). */
export const ForgotPasswordSchema = z.object({
    email: z.string().trim().toLowerCase().max(254).email(),
});
export const ResetPasswordSchema = z.object({
    email: z.string().trim().toLowerCase().max(254).email(),
    code: z.string().length(6),
    password: z.string().min(8),
});
//# sourceMappingURL=auth.js.map